import { z } from 'zod';
import { CognitoSubSchema, CursorSchema, EmailSchema, PaginationLimitSchema, RoleSchema, SlugSchema, TenantRoleSchema, TenantStatusSchema, TimestampSchema, UserStatusSchema, paginatedSchema, } from '../common.js';
import { InviteSummarySchema } from '../entities/invite.js';
import { SuperAdminInviteSummarySchema, SuperAdminSchema } from '../entities/superAdmin.js';
import { TenantSchema } from '../entities/tenant.js';
import { TenantUserSchema } from '../entities/tenantUser.js';
import { TenantLogoUploadUrlRequestSchema, TenantLogoUploadUrlResponseSchema } from './tenant.js';
import { TenantUsersInviteRequestSchema, TenantUsersInviteResponseSchema, TenantUsersInvitesListQuerySchema, TenantUsersInvitesListResponseSchema, } from './users.js';
// =========================================================
// /v1/admin/tenants — see docs/api.md §3.6
// =========================================================
// GET /v1/admin/tenants
export const AdminTenantsListQuerySchema = z.object({
    search: z.string().optional(),
    status: TenantStatusSchema.optional(),
    limit: PaginationLimitSchema.optional(),
    cursor: CursorSchema.optional(),
});
export const AdminTenantSchema = TenantSchema.extend({
    activeAgentCount: z.number().int().nonnegative().default(0),
});
export const AdminTenantsListResponseSchema = paginatedSchema(AdminTenantSchema);
// POST /v1/admin/tenants
export const AdminTenantsCreateRequestSchema = z.object({
    name: z.string().min(1).max(120),
    slug: SlugSchema,
    firstAdmin: z.object({
        email: EmailSchema,
        firstName: z.string().min(1).max(60),
        lastName: z.string().min(1).max(60),
    }),
});
export const AdminTenantsCreateResponseSchema = z.object({
    tenant: TenantSchema,
    firstAdminInvite: InviteSummarySchema,
});
// GET /v1/admin/tenants/{tenantId}
export const AdminTenantsGetResponseSchema = AdminTenantSchema;
// PATCH /v1/admin/tenants/{tenantId}
export const AdminTenantsPatchRequestSchema = z
    .object({
    name: z.string().min(1).max(120).optional(),
    status: TenantStatusSchema.optional(),
    logoUrl: TenantSchema.shape.logoUrl.optional(),
    brandColor: TenantSchema.shape.brandColor.optional(),
    notes: z.string().max(2000).nullable().optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'At least one field required.' });
export const AdminTenantsPatchResponseSchema = TenantSchema;
// DELETE /v1/admin/tenants/{tenantId} — 204 no body (soft-delete sets status=deleted)
// GET /v1/admin/tenants/{tenantId}/users
export const AdminTenantUsersListQuerySchema = z.object({
    status: UserStatusSchema.optional(),
    limit: PaginationLimitSchema.optional(),
    cursor: CursorSchema.optional(),
});
export const AdminTenantUsersListResponseSchema = paginatedSchema(TenantUserSchema);
// POST /v1/admin/tenants/{tenantId}/logo-upload-url
export const AdminTenantLogoUploadUrlRequestSchema = TenantLogoUploadUrlRequestSchema;
export const AdminTenantLogoUploadUrlResponseSchema = TenantLogoUploadUrlResponseSchema;
// GET /v1/admin/tenants/{tenantId}/invites
export const AdminTenantInvitesListQuerySchema = TenantUsersInvitesListQuerySchema;
export const AdminTenantInvitesListResponseSchema = TenantUsersInvitesListResponseSchema;
// POST /v1/admin/tenants/{tenantId}/invites
export const AdminTenantInviteRequestSchema = TenantUsersInviteRequestSchema;
export const AdminTenantInviteResponseSchema = TenantUsersInviteResponseSchema;
// =========================================================
// /v1/admin/users (cross-tenant) — see docs/api.md §3.7
// =========================================================
// GET /v1/admin/users
/**
 * Cross-tenant user search (super-admin).
 *
 * - `email` stays supported for back-compat with the prior exact-match
 *   contract; if supplied it short-circuits to the GSI3 lookup.
 * - `search` is a ≥2-char substring match across email + `firstName lastName`
 *   (case-insensitive). Uses a GSI2 scan-index query + in-memory filter — no
 *   DynamoDB Scan, per CLAUDE.md §8.
 * - `role`, `tenantId`, `status` are exact filters, combined with AND. `role`
 *   accepts `super-admin` too so the backoffice can look across tenant users
 *   and super-admins if future UI needs it (Phase 1 only ever returns tenant
 *   users from this endpoint — super-admins live under a separate PK).
 * - `email` and `search` are mutually exclusive at the handler level.
 */
export const AdminUsersSearchQuerySchema = z
    .object({
    email: EmailSchema.optional(),
    search: z.string().min(2).max(120).optional(),
    role: RoleSchema.optional(),
    tenantId: z.string().uuid().optional(),
    status: UserStatusSchema.optional(),
    limit: PaginationLimitSchema.optional(),
    cursor: CursorSchema.optional(),
})
    .refine((v) => !(v.email !== undefined && v.search !== undefined), {
    message: '`email` and `search` are mutually exclusive.',
});
export const AdminUserSearchHitSchema = z.object({
    userId: CognitoSubSchema,
    email: EmailSchema,
    tenantId: z.string().uuid(),
    tenantName: z.string(),
    role: TenantRoleSchema,
    status: UserStatusSchema,
});
export const AdminUsersSearchResponseSchema = paginatedSchema(AdminUserSearchHitSchema);
// GET /v1/admin/users/{userId}
export const AdminUsersGetResponseSchema = TenantUserSchema;
// PATCH /v1/admin/users/{userId}
export const AdminUsersPatchRequestSchema = z
    .object({
    role: TenantRoleSchema.optional(),
    status: z.enum(['active', 'disabled']).optional(),
})
    .refine((v) => Object.keys(v).length > 0, { message: 'At least one field required.' });
export const AdminUsersPatchResponseSchema = TenantUserSchema;
// POST /v1/admin/users/{userId}/reset-password — 204 no body
// DELETE /v1/admin/users/{userId} — 204 no body
// GET /v1/admin/users/{userId}/login-history
/**
 * One auth event from Cognito's `AdminListUserAuthEvents` API.
 *
 * Fields mirror the `AuthEventType` shape but are de-AWS-ified for the client:
 * timestamps come back as ISO-8601 strings (Cognito returns Date objects over
 * the SDK, epoch-ms on the wire) and optional fields are `null` rather than
 * missing.
 */
export const LoginHistoryChallengeSchema = z.object({
    challengeName: z.string(),
    challengeResponse: z.string(),
});
export const LoginHistoryEventSchema = z.object({
    eventId: z.string(),
    /**
     * Cognito event types (phase 1 surface): SignIn, PasswordChange, ResendCode,
     * SignUp. Unknown future values fall through as the raw string.
     */
    eventType: z.string(),
    /** Pass | Fail per Cognito; null when the API doesn't return one. */
    eventResponse: z.string().nullable(),
    /** ISO-8601 UTC — transformed from Cognito's `CreationDate` Date object. */
    creationDate: TimestampSchema.nullable(),
    ipAddress: z.string().nullable(),
    city: z.string().nullable(),
    country: z.string().nullable(),
    deviceName: z.string().nullable(),
    challenges: z.array(LoginHistoryChallengeSchema),
});
export const AdminUsersLoginHistoryQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(60).optional(),
});
export const AdminUsersLoginHistoryResponseSchema = z.object({
    events: z.array(LoginHistoryEventSchema),
});
// =========================================================
// /v1/admin/super-admins — see docs/api.md §3.8
// =========================================================
// GET /v1/admin/super-admins
export const AdminSuperAdminsListQuerySchema = z.object({
    limit: PaginationLimitSchema.optional(),
    cursor: CursorSchema.optional(),
});
export const AdminSuperAdminsListResponseSchema = paginatedSchema(SuperAdminSchema);
// POST /v1/admin/super-admins/invite
export const AdminSuperAdminsInviteRequestSchema = z.object({
    email: EmailSchema,
    firstName: z.string().min(1).max(60),
    lastName: z.string().min(1).max(60),
});
export const AdminSuperAdminsInviteResponseSchema = z.object({
    invite: SuperAdminInviteSummarySchema,
});
// PATCH /v1/admin/super-admins/{userId}
export const AdminSuperAdminsPatchRequestSchema = z.object({
    status: z.enum(['active', 'disabled']),
});
export const AdminSuperAdminsPatchResponseSchema = SuperAdminSchema;
// POST /v1/admin/super-admins/{userId}/reset-password — 204 no body
// DELETE /v1/admin/super-admins/{userId} — 204 no body
// POST /v1/admin/tenants/{tenantId}/invites/{inviteId}/resend — 204 no body
//# sourceMappingURL=admin.js.map